import type { GetHigherKeyResType } from "../keyboard/advancedKey";
import type {
  CustomLightingData,
  GetLightingBaseResType,
} from "../keyboard/lighting";

/** 触发配置分组（相同配置的按键分为一组，仅星闪） */
export interface TriggerConfigGroup {
  keyPositions: Array<{
    row: number;
    col: number;
    keyName?: string;
    keyCode?: number;
  }>;
  mode: number;
  normalPress?: number;
  normalRelease?: number;
  rtFirstTouch?: number;
  rtPress?: number;
  rtRelease?: number;
  pressDeadStroke?: number;
  releaseDeadStroke?: number;
  axis?: number;
  calibrate?: number;
}

export interface ChangeKeyEntry {
  row: number;
  col: number;
  keyCode?: number;
  keyName?: string;
  sourceKeyName?: string;
}

export interface ChangeKeyLayer {
  system: "WIN" | "MAC";
  layer: number;
  /** 该层全部有效键位（非仅差异项），供社区分享/预览/应用完整还原 */
  keys: ChangeKeyEntry[];
}

/** 性能页配置（字段因方案而异） */
export type PerformanceConfig = Record<string, unknown>;

/** 板载完整配置 — 社区 configPayload 与此结构对齐 */
export interface FullBoardConfigData {
  lighting: {
    base?: GetLightingBaseResType;
    /** 灯带常规灯效（getLightingBase area=Decorate1 / 贝盈 Side 等） */
    decorate1Base?: GetLightingBaseResType;
    decorate1?: CustomLightingData;
  };
  trigger: TriggerConfigGroup[];
  advancedKey: Map<string, GetHigherKeyResType>;
  changeKey?: ChangeKeyLayer[];
  performance?: PerformanceConfig;
}

export function createEmptyBoardConfig(): FullBoardConfigData {
  return {
    lighting: {},
    trigger: [],
    advancedKey: new Map(),
    changeKey: [],
    performance: {},
  };
}

export function serializeConfigData(
  configData: FullBoardConfigData
): Record<string, unknown> {
  return {
    lighting: {
      base: configData.lighting?.base,
      decorate1Base: configData.lighting?.decorate1Base,
      decorate1: configData.lighting?.decorate1,
    },
    trigger: configData.trigger,
    advancedKey: Array.from(configData.advancedKey.entries()),
    changeKey: configData.changeKey ?? [],
    performance: configData.performance ?? {},
  };
}

export function deserializeConfigData(
  serializedData: Record<string, unknown> | null | undefined
): FullBoardConfigData {
  if (!serializedData || typeof serializedData !== "object") {
    return createEmptyBoardConfig();
  }
  const data = serializedData as {
    lighting?: FullBoardConfigData["lighting"];
    trigger?: TriggerConfigGroup[];
    advancedKey?:
      | [string, GetHigherKeyResType][]
      | Record<string, GetHigherKeyResType>;
    changeKey?: ChangeKeyLayer[];
    performance?: PerformanceConfig;
  };

  let advancedKeyMap: FullBoardConfigData["advancedKey"];
  const rawAdvanced = data.advancedKey;
  if (rawAdvanced instanceof Map) {
    advancedKeyMap = rawAdvanced as FullBoardConfigData["advancedKey"];
  } else if (Array.isArray(rawAdvanced)) {
    advancedKeyMap = new Map(rawAdvanced) as FullBoardConfigData["advancedKey"];
  } else if (rawAdvanced && typeof rawAdvanced === "object") {
    advancedKeyMap = new Map(
      Object.entries(rawAdvanced)
    ) as FullBoardConfigData["advancedKey"];
  } else {
    advancedKeyMap = new Map() as FullBoardConfigData["advancedKey"];
  }

  return {
    lighting: data.lighting || {},
    trigger: Array.isArray(data.trigger) ? data.trigger : [],
    advancedKey: advancedKeyMap,
    changeKey: data.changeKey ?? [],
    performance: data.performance ?? {},
  };
}
